import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Paper, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAppDispatch, useAppSelector } from "../../../../state/state.tsx";
import { openModal } from "../../../../state/ModalSlice/ModalSlice.ts";
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2';
import { collection, query, orderBy, Timestamp, doc, updateDoc, limit, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { toast } from 'react-toastify';

interface Transaction {
    id?: string;
    phoneNumber?: string;
    countryCode?: string;
    payerName: string;
    type: 'Credit' | 'Debit';
    amount: number;
    status: 'Pending' | 'Completed' | 'Failed';
    createdAt: Timestamp;
    description?: string;
    category?: string;
}

export default function MoneyTrail() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'Pending' | 'Completed' | 'Failed'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const dispatch = useAppDispatch();

  // Get the service provider ID
  const serviceProviderId = useAppSelector(selectEffectiveUID);

  useEffect(() => {
    if (!serviceProviderId) return;
    setIsLoading(true);
    
    // Set up real-time listener
    const transactionsRef = collection(db, 'ServiceProviders', serviceProviderId, 'Adhoc');
    const q = query(
      transactionsRef, 
      orderBy('createdAt', 'desc'),
      limit(rowsPerPage)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const loadedTransactions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Transaction[];

      setTransactions(loadedTransactions);
      setIsLoading(false);

      // Get total count
      getDocs(transactionsRef).then(snapshot => {
        setTotalCount(snapshot.size);
      });
    }, (error) => {
      console.error('Error listening to transactions:', error);
      toast.error('Failed to load transactions');
      setIsLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [serviceProviderId, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditTransaction = async () => {
    if (!serviceProviderId || !editingTransaction?.id) return;
    
    try {
      if (!editingTransaction.phoneNumber || !editingTransaction.payerName || !editingTransaction.amount) {
        toast.error('Please fill in all required fields');
        return;
      }

      const transactionRef = doc(db, 'ServiceProviders', serviceProviderId, 'Adhoc', editingTransaction.id);
      await updateDoc(transactionRef, {
        phoneNumber: editingTransaction.phoneNumber,
        payerName: editingTransaction.payerName,
        type: editingTransaction.type,
        amount: Number(editingTransaction.amount),
        status: editingTransaction.status,
        description: editingTransaction.description,
        countryCode: editingTransaction.countryCode
      });
      
      toast.success('Transaction updated successfully');
      setShowEditModal(false);
      setEditingTransaction(null);
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error('Failed to update transaction');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Open modal handlers
  const handleOpenPaymentRequestModal = () => {
    dispatch(openModal("PaymentRequestModalOpen"));
  };

  const handleOpenRecordPaymentModal = () => {
    dispatch(openModal("RecordManualPaymentModalOpen"));
  };

  const handleOpenRecordExpenseModal = () => {
    dispatch(openModal("RecordExpenseModalOpen"));
  };

  // Get unique categories from transactions
  const getUniqueCategories = (): string[] => {
    const categories = transactions
      .map(t => t.category)
      .filter((category): category is string => Boolean(category && category.trim() !== ''))
      .filter((category, index, arr) => arr.indexOf(category) === index)
      .sort();
    return categories;
  };

  // Update the filtered transactions logic
  const filteredTransactions = transactions
    .filter(transaction => {
      // Search filter
      const matchesSearch = 
        transaction.payerName?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        transaction.phoneNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        transaction.description?.toLowerCase().includes(searchQuery.toLowerCase());

      // Status filter
      const matchesStatus = filterStatus === 'all' || transaction.status === filterStatus;

      // Category filter
      const matchesCategory = filterCategory === 'all' || transaction.category === filterCategory;

      // Time period filter
      const now = new Date();
      const transactionDate = transaction.createdAt.toDate();
      let matchesTime = true;

      switch (filterPeriod) {
        case 'today':
          matchesTime = transactionDate.toDateString() === now.toDateString();
          break;
        case 'week': {
          const weekAgo = new Date(now.setDate(now.getDate() - 7));
          matchesTime = transactionDate >= weekAgo;
          break;
        }
        case 'month': {
          const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
          matchesTime = transactionDate >= monthAgo;
          break;
        }
        default:
          matchesTime = true;
      }

      return matchesSearch && matchesStatus && matchesCategory && matchesTime;
    });

  return (
    <div className="container mx-auto px-4 py-8">
      <Typography variant="h4" component="h1" gutterBottom>
        Collect+
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" className="mb-6">
        Collect Money From Anyone Globally!!
      </Typography>

      {/* Action Buttons */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<WhatsAppIcon />}
          onClick={handleOpenPaymentRequestModal}
        >
          Send Payment Request
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleOpenRecordPaymentModal}
        >
          Record Payment
        </Button>
        <Button 
          variant="contained" 
          color="secondary" 
          startIcon={<AddIcon />}
          onClick={handleOpenRecordExpenseModal}
        >
          Record Expense
        </Button>
        <Button 
          variant="outlined" 
          color="primary" 
          startIcon={<SettingsIcon />}
          onClick={() => dispatch(openModal("PaymentHeadersModalOpen"))}
        >
          Manage Categories
        </Button>
      </Box>

      <Paper className="mb-6">
        {/* Filter and Search Controls */}
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            style={{ maxWidth: '300px' }}
          />
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ mr: 1, alignSelf: 'center' }}>Time Period:</Typography>
            <Chip
              label="All Time"
              onClick={() => setFilterPeriod('all')}
              color={filterPeriod === 'all' ? 'primary' : 'default'}
              variant={filterPeriod === 'all' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Today"
              onClick={() => setFilterPeriod('today')}
              color={filterPeriod === 'today' ? 'primary' : 'default'}
              variant={filterPeriod === 'today' ? 'filled' : 'outlined'}
            />
            <Chip
              label="This Week"
              onClick={() => setFilterPeriod('week')}
              color={filterPeriod === 'week' ? 'primary' : 'default'}
              variant={filterPeriod === 'week' ? 'filled' : 'outlined'}
            />
            <Chip
              label="This Month"
              onClick={() => setFilterPeriod('month')}
              color={filterPeriod === 'month' ? 'primary' : 'default'}
              variant={filterPeriod === 'month' ? 'filled' : 'outlined'}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ mr: 1, alignSelf: 'center' }}>Status:</Typography>
            <Chip
              label="All"
              onClick={() => setFilterStatus('all')}
              color={filterStatus === 'all' ? 'primary' : 'default'}
              variant={filterStatus === 'all' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Pending"
              onClick={() => setFilterStatus('Pending')}
              color={filterStatus === 'Pending' ? 'warning' : 'default'}
              variant={filterStatus === 'Pending' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Completed"
              onClick={() => setFilterStatus('Completed')}
              color={filterStatus === 'Completed' ? 'success' : 'default'}
              variant={filterStatus === 'Completed' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Failed"
              onClick={() => setFilterStatus('Failed')}
              color={filterStatus === 'Failed' ? 'error' : 'default'}
              variant={filterStatus === 'Failed' ? 'filled' : 'outlined'}
            />
          </Box>

          {/* Category Filter */}
          {getUniqueCategories().length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ mr: 1, alignSelf: 'center' }}>Category:</Typography>
              <Chip
                label="All Categories"
                onClick={() => setFilterCategory('all')}
                color={filterCategory === 'all' ? 'primary' : 'default'}
                variant={filterCategory === 'all' ? 'filled' : 'outlined'}
              />
              {getUniqueCategories().map((category) => (
                <Chip
                  key={category}
                  label={category}
                  onClick={() => setFilterCategory(category)}
                  color={filterCategory === category ? 'primary' : 'default'}
                  variant={filterCategory === category ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          )}
        </Box>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Category</TableCell>
                    <TableCell>Payer</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                      <TableCell>{transaction.createdAt.toDate().toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={transaction.type} 
                          color={transaction.type === 'Credit' ? 'success' : 'error'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      {transaction.category ? (
                        <Chip 
                          label={transaction.category} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                      <TableCell>{transaction.payerName || '-'}</TableCell>
                      <TableCell>{transaction.phoneNumber || '-'}</TableCell>
                      <TableCell>{transaction.description || '-'}</TableCell>
                      <TableCell align="right" sx={{ 
                        color: transaction.type === 'Credit' ? 'success.main' : 'error.main',
                        fontWeight: 'bold'
                      }}>
                        {transaction.type === 'Credit' ? '+' : '-'}₹{transaction.amount.toLocaleString()}
                      </TableCell>
                    <TableCell>
                      <Chip 
                        label={transaction.status} 
                        color={getStatusColor(transaction.status)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                        <button
                          onClick={() => {
                            setEditingTransaction(transaction);
                            setShowEditModal(true);
                          }}
                          className="p-1 hover:bg-gray-100 rounded-full"
                          title="Edit Transaction"
                        >
                          <EditIcon fontSize="small" />
                        </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Edit Transaction Modal */}
      {showEditModal && editingTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Edit Transaction</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Payer Name</label>
                <input
                  type="text"
                  value={editingTransaction.payerName}
                  onChange={(e) => setEditingTransaction({
                    ...editingTransaction,
                    payerName: e.target.value
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                <input
                  type="tel"
                  value={editingTransaction.phoneNumber}
                  onChange={(e) => setEditingTransaction({
                    ...editingTransaction,
                    phoneNumber: e.target.value
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <input
                  type="number"
                  value={editingTransaction.amount}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    if (!isNaN(value)) {
                      setEditingTransaction({
                        ...editingTransaction,
                        amount: value
                      });
                    }
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={editingTransaction.status}
                  onChange={(e) => setEditingTransaction({
                    ...editingTransaction,
                    status: e.target.value as 'Pending' | 'Completed' | 'Failed'
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="Completed">Completed</option>
                  <option value="Pending">Pending</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={editingTransaction.description || ''}
                  onChange={(e) => setEditingTransaction({
                    ...editingTransaction,
                    description: e.target.value
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  rows={3}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingTransaction(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditTransaction}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Update Transaction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 